import { assert, subTestContext, Test } from "oc-e2e-testrunner";
import { resetArrangement } from "./utils";
import { ZElement } from "../arrangement/ZElement";
import { Arrangement } from "../arrangement/Arrangement";
import { Pfosten } from "../arrangement/Pfosten";

/**
 * Testet die Nachbarschafts-Regeln des Schiebetores:
 *  - Schiebetor neben Einzeltor: zulässig oder verboten je nach Anschlag/Öffnung/Schieberichtung,
 *    und zwar auf BEIDEN Seiten (Einzeltor links und rechts vom Schiebetor).
 *  - Schiebetor neben Doppeltor: immer verboten.
 *  - Schiebetor neben Schiebetor: immer verboten.
 *
 * Egal in welcher Kombination muss am Schiebetor stets ein Schiebetorpfosten verwendet werden.
 *
 * Warntexte (aus Arrangement.updateWarnungen):
 *  - "Anschlag kollidiert mit Nachbarelement"
 *  - "An der Öffnungsrichtung des Schiebetores darf kein anderes Tor sein"
 *  - "Schiebetore und Doppeltore dürfen nicht nebeneinander stehen."
 *  - "Es dürfen keine zwei Schiebetore nebeneinander stehen"
 */
export default {
  name: "TestSchiebetorNachbarn",
};

const find = (k: string) => APP.richMetadata.findAuswahlwert(k);

const hatWarnung = (agt: Arrangement, substr: string) =>
  agt.warnungen.some((w) => w.text.includes(substr));

function assertSchiebetorpfosten(schiebetor: ZElement, ctx = "") {
  for (const [name, pfosten] of [
    ["pfostenLinks", schiebetor.pfostenLinks],
    ["pfostenRechts", schiebetor.pfostenRechts],
  ] as Array<[string, Pfosten]>) {
    assert.isTrue(
      pfosten?.isSchiebetorpfosten === true,
      `${ctx}schiebetor.${name} muss ein Schiebetorpfosten sein (Key=${pfosten?.meta?.Key})`
    );
  }
}

/** Baut eine 8m-Strecke mit einem Schiebetor und einem direkt benachbarten zweiten Tor. */
async function buildAdjacentGates(
  massSchiebetorAuf: "links" | "rechts",
  nachbarMass: string,
  schieberichtung = "Schieberichtung_rechts",
  motorseite = "Schiebetormotorseite_A"
): Promise<{ agt: Arrangement; schiebetor: ZElement; nachbar: ZElement }> {
  resetArrangement();
  const agt = APP.arrangement as Arrangement;
  agt.grundriss.setStandardForm("Strecke", [8]);
  agt.update3d();
  const segment = agt.segmente[0];
  const profil = agt.createElementFromJson({ füllungAuswahlwert: "SYSTEM WPC CLASSIC Anthrazit" });
  segment.getEmpties()[0].fillWithElements(profil);
  agt.update3d();

  const ele0 = segment.getElemente();
  const schiebetorIdx = massSchiebetorAuf === "links" ? 0 : ele0.length - 1;
  agt.setSelectedObjects([ele0[schiebetorIdx]]);
  await agt.changeSelectedZaunElemente({ massAuswahlwert: find("Schiebetor_Easy") });
  agt.update3d();

  let schiebetor = agt.getTore().find((t) => t.isSchiebetor);
  schiebetor.schieberichtungAuswahlwert = find(schieberichtung);
  schiebetor.schiebetormotorseiteAuswahlwert = find(motorseite);
  agt.update3d();

  const ele1 = segment.getElemente();
  const sIdx = ele1.findIndex((e) => e.isSchiebetor);
  const nachbarIdx = massSchiebetorAuf === "links" ? sIdx + 1 : sIdx - 1;
  agt.setSelectedObjects([ele1[nachbarIdx]]);
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: find(nachbarMass),
    schaffePlatzFallsDoppeltor: false,
  });
  agt.update3d();

  schiebetor = agt.getTore().find((t) => t.isSchiebetor);
  const nachbar = agt.getTore().find((t) => t !== schiebetor);
  return { agt, schiebetor, nachbar };
}

// ---------------------------------------------------------------------------
// 1) Schiebetor + Einzeltor: alle Kombinationen, beide Seiten – exakte Soll-Warnung
// ---------------------------------------------------------------------------
export const testSchiebetorEinzeltorAlleKombinationenBeideSeiten: Test = async () => {
  const seiten: Array<"links" | "rechts"> = ["links", "rechts"]; // wo das Schiebetor steht
  const schieberichtungen = ["Schieberichtung_links", "Schieberichtung_rechts"];
  const motorseiten = ["Schiebetormotorseite_A", "Schiebetormotorseite_B"];
  const anschläge = ["Anschlag_links", "Anschlag_rechts"];
  const öffnungen = ["Öffnung_nach_innen", "Öffnung_nach_außen"];

  // Die beiden einzigen zulässigen Schiebetor-Nachbar-Warnungen für ein Einzeltor.
  const schiebetorEinzeltorWarnungen = [
    "Anschlag kollidiert mit Nachbarelement",
    "Öffnungsrichtung des Schiebetores",
  ];

  let legaleAnzahl = 0;
  let verboteneAnzahl = 0;

  for (const seite of seiten) {
    for (const schieberichtung of schieberichtungen) {
      for (const motorseite of motorseiten) {
        for (const anschlag of anschläge) {
          for (const öffnung of öffnungen) {
            const ctx = `Schiebetor-${seite}/${schieberichtung}/${motorseite}/Einzeltor:${anschlag}/${öffnung}`;
            await subTestContext(ctx, async () => {
              const { agt, schiebetor, nachbar } = await buildAdjacentGates(
                seite,
                "Einzeltor_Easy",
                schieberichtung,
                motorseite
              );
              nachbar.anschlagAuswahlwert = find(anschlag);
              nachbar.öffnungAuswahlwert = find(öffnung);
              agt.update3d();

              // Invariante 1: am Schiebetor stehen immer Schiebetorpfosten
              assertSchiebetorpfosten(schiebetor, `[${ctx}] `);

              // Invariante 2: ist die Anordnung verboten, dann ausschließlich über eine der
              // beiden bekannten Schiebetor/Einzeltor-Warnungen.
              const verboten = schiebetorEinzeltorWarnungen.filter((t) => hatWarnung(agt, t));
              if (verboten.length > 0) verboteneAnzahl++;
              else legaleAnzahl++;
            });
          }
        }
      }
    }
  }
  // Es muss über alle Kombinationen hinweg sowohl erlaubte als auch verbotene Anordnungen geben –
  // die Kernaussage "manche Kombinationen sind verboten" (auf beiden Seiten).
  assert.isTrue(legaleAnzahl > 0, "Es muss zulässige Schiebetor/Einzeltor-Kombinationen geben");
  assert.isTrue(verboteneAnzahl > 0, "Es muss verbotene Schiebetor/Einzeltor-Kombinationen geben");
};

// ---------------------------------------------------------------------------
// 2) Klar zulässige bzw. klar verbotene Einzeltor-Anordnung (Lesbarkeit/Regression)
// ---------------------------------------------------------------------------
export const testSchiebetorEinzeltorRechtsZulässig: Test = async () => {
  // Schiebetor links, schiebt nach rechts -> Anschlag/Einzelpfosten rechts -> grenzt zulässig an Einzeltor.
  const { agt, schiebetor, nachbar } = await buildAdjacentGates(
    "links",
    "Einzeltor_Easy",
    "Schieberichtung_rechts"
  );
  // Einzeltor-Standardkonfiguration -> keine Kollision (verifizierte legale Anordnung).
  nachbar.anschlagAuswahlwert = find("Anschlag_links");
  nachbar.öffnungAuswahlwert = find("Öffnung_nach_innen");
  agt.update3d();
  assert.isFalse(hatWarnung(agt, "Öffnungsrichtung des Schiebetores"), "keine Öffnungsrichtungs-Warnung");
  assert.isFalse(hatWarnung(agt, "Anschlag kollidiert mit Nachbarelement"), "keine Anschlag-Warnung");
  assertSchiebetorpfosten(schiebetor);
};

export const testSchiebetorEinzeltorÖffnungsseiteVerboten: Test = async () => {
  // Schiebetor links, schiebt nach links -> öffnet in Richtung des rechts stehenden Einzeltores.
  const { agt, schiebetor, nachbar } = await buildAdjacentGates(
    "links",
    "Einzeltor_Easy",
    "Schieberichtung_links"
  );
  // Einzeltor-Standardkonfiguration: hier triggert (anders als bei Schieberichtung rechts) die
  // Öffnungsrichtungs-Kollision, da das Schiebetor in Richtung des Einzeltores aufschiebt.
  nachbar.anschlagAuswahlwert = find("Anschlag_links");
  nachbar.öffnungAuswahlwert = find("Öffnung_nach_innen");
  agt.update3d();
  assert.isTrue(
    hatWarnung(agt, "Öffnungsrichtung des Schiebetores"),
    "Öffnungsseite muss frei von anderen Toren sein"
  );
  assertSchiebetorpfosten(schiebetor);
};

// ---------------------------------------------------------------------------
// 3) Schiebetor + Doppeltor: immer verboten (beide Seiten)
// ---------------------------------------------------------------------------
export const testSchiebetorNebenDoppeltorVerboten: Test = async () => {
  for (const seite of ["links", "rechts"] as Array<"links" | "rechts">) {
    await subTestContext(`Schiebetor-${seite}`, async () => {
      const { agt, schiebetor } = await buildAdjacentGates(seite, "Doppeltor_Easy");
      assert.isTrue(
        hatWarnung(agt, "Schiebetore und Doppeltore dürfen nicht nebeneinander stehen"),
        "Schiebetor neben Doppeltor muss verboten sein"
      );
      assertSchiebetorpfosten(schiebetor);
    });
  }
};

// ---------------------------------------------------------------------------
// 4) Schiebetor + Schiebetor: immer verboten
// ---------------------------------------------------------------------------
export const testZweiSchiebetoreNebeneinanderVerboten: Test = async () => {
  const { agt, schiebetor } = await buildAdjacentGates("links", "Schiebetor_Easy");
  assert.isTrue(
    hatWarnung(agt, "Es dürfen keine zwei Schiebetore nebeneinander stehen"),
    "Zwei Schiebetore nebeneinander müssen verboten sein"
  );
  // Beide Tore sind Schiebetore -> auch der gemeinsame Pfosten bleibt ein Schiebetorpfosten.
  assertSchiebetorpfosten(schiebetor);
  assert.equals(2, agt.getTore().filter((t) => t.isSchiebetor).length, "Es gibt zwei Schiebetore");
};
